import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FeatherAlertTriangle, FeatherPlus, FeatherTrash2, FeatherX } from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Select } from "../../../../ui/components/Select";
import { Tabs } from "../../../../ui/components/Tabs";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { useAuth } from "../../../auth";
import {
  deactivateTeamMember,
  fetchTeamBoardState,
  fetchTeamRoles,
  saveTeamBoard,
  updateTeamMember,
  type BoardMember,
  type BoardTab,
  type SeatKind,
  type TabPolicy,
  type TeamBoardState,
  type TeamRole,
} from "../../../../shared/data/api/teamApi";

/**
 * TeamTabsBoard — the tenant's maker–checker screen, the same six-tab board
 * master-admin runs at onboarding: per tab a pool of Makers, a pool of
 * Checkers, and a three-way policy for every function the tab covers.
 *
 * Edits stay LOCAL until Save: the backend has no staging, so one atomic
 * save (POST /api/team-assignment/save) derives the ZN roles, checker pools
 * and reviewer fallbacks in a single transaction. Server-side gates refuse a
 * save that would strand reviews (a Maker with review functions and no
 * Checker); the same gates are mirrored here for live feedback.
 */

const POLICY_LABEL: Record<TabPolicy, string> = {
  post: "Post directly",
  review: "Needs review",
  blocked: "Blocked",
};

const POLICY_HINT: Record<TabPolicy, string> = {
  post: "Makers do it themselves, nothing waits.",
  review: "A Checker on this tab clears it before it is final.",
  blocked: "Checkers only — hidden from Makers.",
};

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/** Editable copy of the board: seats per tab, policy per function. */
interface Draft {
  seats: Record<string, Record<number, SeatKind>>;
  policies: Record<string, TabPolicy>;
}

const draftFromState = (state: TeamBoardState): Draft => {
  const seats: Draft["seats"] = {};
  for (const tab of state.tabs) {
    seats[tab.key] = {};
    for (const p of tab.makers) seats[tab.key][p.userId] = "maker";
    for (const p of tab.checkers) seats[tab.key][p.userId] = "checker";
  }
  const policies: Draft["policies"] = {};
  for (const tab of state.tabs) {
    for (const fn of tab.functions) policies[fn.key] = fn.policy;
  }
  return { seats, policies };
};

/** The server's gate rule, mirrored for live feedback while editing. */
const tabGate = (
  tab: BoardTab,
  draft: Draft,
): { status: "ok" | "empty" | "block" | "warn"; message: string | null } => {
  const seatEntries = Object.entries(draft.seats[tab.key] ?? {});
  const makers = seatEntries.filter(([, seat]) => seat === "maker").length;
  const checkers = seatEntries.filter(([, seat]) => seat === "checker").length;
  const reviews = tab.functions.filter((fn) => draft.policies[fn.key] === "review").length;

  if (makers === 0 && checkers === 0) {
    return { status: "empty", message: null };
  }
  if (makers > 0 && reviews > 0 && checkers === 0) {
    return {
      status: "block",
      message: `${tab.label} has review functions but no Checker. Add a Checker, or set those functions to post directly.`,
    };
  }
  if (checkers > 0 && makers === 0) {
    return {
      status: "warn",
      message: `${tab.label} has Checkers but no Maker — they would be reviewing their own entries.`,
    };
  }
  return { status: "ok", message: null };
};

const SeatColumn: React.FC<{
  title: string;
  hint: string;
  seat: SeatKind;
  tabKey: string;
  draft: Draft;
  members: BoardMember[];
  disabled: boolean;
  onChange: (next: Draft) => void;
}> = ({ title, hint, seat, tabKey, draft, members, disabled, onChange }) => {
  const tabSeats = draft.seats[tabKey] ?? {};
  const seated = members.filter((m) => tabSeats[m.userId] === seat);
  // Anyone active, not the firm admin (the backend refuses seating them),
  // not a client_view login (client dashboard accounts, managed from master —
  // seating one would replace client_view with a ZN role and kill their
  // dashboard access), and not already on this seat of this tab. Someone on
  // the OTHER seat may be picked — that moves them (one seat per person per
  // tab).
  const candidates = members.filter(
    (m) =>
      m.role.toLowerCase() !== "admin" &&
      m.role.toLowerCase() !== "client_view" &&
      tabSeats[m.userId] !== seat,
  );

  const setSeat = (userId: number) => {
    onChange({
      ...draft,
      seats: { ...draft.seats, [tabKey]: { ...tabSeats, [userId]: seat } },
    });
  };
  const removeSeat = (userId: number) => {
    const next = { ...tabSeats };
    delete next[userId];
    onChange({ ...draft, seats: { ...draft.seats, [tabKey]: next } });
  };

  return (
    <div className="flex grow shrink-0 basis-0 flex-col gap-2 rounded-md border border-solid border-neutral-border px-4 py-4">
      <div className="flex flex-col">
        <span className="text-body-bold font-body-bold text-default-font">{title}</span>
        <span className="text-caption font-caption text-subtext-color">{hint}</span>
      </div>

      {seated.map((m) => (
        <div key={m.userId} className="flex w-full items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-caption-bold font-caption-bold text-subtext-color">
            {initials(m.name)}
          </span>
          <div className="flex grow shrink-0 basis-0 flex-col">
            <span className="text-body font-body text-default-font">{m.name}</span>
            <span className="text-caption font-caption text-subtext-color">{m.email}</span>
          </div>
          <IconButton
            size="small"
            icon={<FeatherX />}
            disabled={disabled}
            onClick={() => removeSeat(m.userId)}
          />
        </div>
      ))}
      {seated.length === 0 && (
        <span className="text-caption font-caption text-subtext-color">Nobody yet.</span>
      )}

      <Select
        className="w-full"
        label=""
        placeholder={`Add a ${title.toLowerCase().replace(/s$/, "")}…`}
        helpText=""
        icon={<FeatherPlus />}
        value=""
        disabled={disabled || candidates.length === 0}
        onValueChange={(value: string) => value && setSeat(Number(value))}
      >
        {candidates.map((m) => (
          <Select.Item key={m.userId} value={String(m.userId)}>
            {m.name}
          </Select.Item>
        ))}
      </Select>
    </div>
  );
};

/** A stock-role user seated on no tab: role picker (stock roles) + remove. */
const UnseatedRow: React.FC<{
  member: BoardMember;
  stockRoles: TeamRole[];
  isSelf: boolean;
  onSaved: (message: string) => void;
  onRemove: (member: BoardMember) => void;
}> = ({ member, stockRoles, isSelf, onSaved, onRemove }) => {
  const current = stockRoles.find((r) => r.name === member.role);
  const [roleId, setRoleId] = useState<number | null>(current?.id ?? null);
  const [saving, setSaving] = useState(false);
  const dirty = roleId !== null && roleId !== (current?.id ?? null);

  const save = async () => {
    if (roleId === null) return;
    setSaving(true);
    try {
      await updateTeamMember({ id: member.userId, role_id: roleId });
      onSaved(`${member.name}'s role updated.`);
    } catch (e: any) {
      onSaved(e?.message || "Could not update the role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-caption-bold font-caption-bold text-subtext-color">
        {initials(member.name)}
      </span>
      <div className="flex grow shrink-0 basis-0 flex-col">
        <span className="text-body-bold font-body-bold text-default-font">
          {member.name}
          {isSelf ? "  (you)" : ""}
        </span>
        <span className="text-caption font-caption text-subtext-color">{member.email}</span>
      </div>
      <Select
        className="w-48"
        label=""
        placeholder="No role"
        helpText=""
        value={roleId === null ? "" : String(roleId)}
        disabled={saving}
        onValueChange={(value: string) => setRoleId(Number(value))}
      >
        {stockRoles.map((role) => (
          <Select.Item key={role.id} value={String(role.id)}>
            {role.name}
          </Select.Item>
        ))}
      </Select>
      {dirty && (
        <Button size="small" loading={saving} onClick={save}>
          Save
        </Button>
      )}
      <IconButton
        icon={<FeatherTrash2 />}
        disabled={isSelf || saving}
        onClick={() => onRemove(member)}
      />
    </div>
  );
};

export const TeamTabsBoard: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<TeamBoardState | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("sales");
  const [stockRoles, setStockRoles] = useState<TeamRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<BoardMember | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [boardState, roles] = await Promise.all([fetchTeamBoardState(), fetchTeamRoles()]);
      setState(boardState);
      const fresh = draftFromState(boardState);
      setDraft(fresh);
      setBaseline(JSON.stringify(fresh));
      setStockRoles(
        roles.filter(
          (r) => !r.name.startsWith("ZN ") && r.name.toLowerCase() !== "client_view",
        ),
      );
    } catch (e: any) {
      setError(e?.message || "Could not load the team board.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = draft !== null && JSON.stringify(draft) !== baseline;

  const gates = useMemo(() => {
    if (!state || !draft) return {} as Record<string, ReturnType<typeof tabGate>>;
    const map: Record<string, ReturnType<typeof tabGate>> = {};
    for (const tab of state.tabs) map[tab.key] = tabGate(tab, draft);
    return map;
  }, [state, draft]);

  const blocked = Object.values(gates).some((g) => g.status === "block");

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const fresh = await saveTeamBoard({ seats: draft.seats, policies: draft.policies });
      setState(fresh);
      const nextDraft = draftFromState(fresh);
      setDraft(nextDraft);
      setBaseline(JSON.stringify(nextDraft));
      setNotice("Team saved — roles, review policies and checker pools are live.");
    } catch (e: any) {
      setError(e?.message || "Could not save the team board.");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!state) return;
    const fresh = draftFromState(state);
    setDraft(fresh);
    setNotice(null);
    setError(null);
  };

  const confirmRemove = async () => {
    if (!removing) return;
    try {
      await deactivateTeamMember(removing.userId);
      setNotice(`${removing.name} was deactivated.`);
      setRemoving(null);
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not deactivate.");
      setRemoving(null);
    }
  };

  if (loading) {
    return <span className="text-body font-body text-subtext-color">Loading the team board…</span>;
  }
  if (!state || !draft) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-body font-body text-error-700">{error ?? "Could not load."}</span>
        <Button variant="neutral-secondary" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  // client_view logins are dashboard accounts managed from master, not team
  // members — hiding them beats rendering a role dropdown that (by design)
  // has no valid option for them.
  const unseatedStaff = state.unseated.filter(
    (m) => m.role.toLowerCase() !== "client_view",
  );

  const tab = state.tabs.find((t) => t.key === activeTab) ?? state.tabs[0];
  const tabSeats = draft.seats[tab.key] ?? {};
  const makerCount = Object.values(tabSeats).filter((s) => s === "maker").length;
  const checkerCount = Object.values(tabSeats).filter((s) => s === "checker").length;
  const gate = gates[tab.key];

  const byModule: Record<string, typeof tab.functions> = {};
  for (const fn of tab.functions) {
    (byModule[fn.moduleLabel] ??= []).push(fn);
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {notice && (
        <div className="flex w-full items-center gap-2 rounded-md bg-success-50 px-4 py-3">
          <span className="grow text-body font-body text-success-800">{notice}</span>
          <IconButton size="small" icon={<FeatherX />} onClick={() => setNotice(null)} />
        </div>
      )}
      {error && (
        <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-4 py-3">
          <FeatherAlertTriangle className="text-error-700" />
          <span className="grow text-body font-body text-error-800">{error}</span>
          <IconButton size="small" icon={<FeatherX />} onClick={() => setError(null)} />
        </div>
      )}

      <Tabs>
        {state.tabs.map((t) => (
          <Tabs.Item key={t.key} active={t.key === tab.key} onClick={() => setActiveTab(t.key)}>
            {t.label}
            {gates[t.key]?.status === "block" ? " ⚠" : ""}
          </Tabs.Item>
        ))}
      </Tabs>

      {gate?.message && (
        <div
          className={
            gate.status === "block"
              ? "flex w-full items-start gap-2 rounded-md bg-error-50 px-4 py-3"
              : "flex w-full items-start gap-2 rounded-md bg-warning-50 px-4 py-3"
          }
        >
          <FeatherAlertTriangle
            className={gate.status === "block" ? "text-error-700" : "text-warning-700"}
          />
          <span className="text-body font-body text-default-font">{gate.message}</span>
        </div>
      )}

      <div className="flex w-full items-start gap-4">
        <SeatColumn
          title="Makers"
          hint="Do the day-to-day work on this tab."
          seat="maker"
          tabKey={tab.key}
          draft={draft}
          members={state.members}
          disabled={saving}
          onChange={setDraft}
        />
        <SeatColumn
          title="Checkers"
          hint="Clear reviews and hold the blocked functions."
          seat="checker"
          tabKey={tab.key}
          draft={draft}
          members={state.members}
          disabled={saving}
          onChange={setDraft}
        />
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-center gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">Functions</span>
          <Badge variant="neutral">
            {tab.functions.filter((fn) => draft.policies[fn.key] === "review").length} need review
          </Badge>
        </div>
        {checkerCount > 0 && makerCount === 0 && (
          <span className="text-caption font-caption text-subtext-color">
            Policies take effect when a maker is seated — showing firm defaults.
          </span>
        )}

        {Object.entries(byModule).map(([moduleLabel, fns]) => (
          <div
            key={moduleLabel}
            className="flex w-full flex-col rounded-md border border-solid border-neutral-border"
          >
            <span className="border-b border-solid border-neutral-border px-4 py-2 text-body-bold font-body-bold text-default-font">
              {moduleLabel}
            </span>
            {fns.map((fn) => {
              const policy = draft.policies[fn.key] ?? fn.default;
              return (
                <div
                  key={fn.key}
                  className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-4 py-2 last:border-b-0"
                >
                  <div className="flex grow shrink-0 basis-0 flex-col">
                    <span className="text-body font-body text-default-font">
                      {fn.label}
                      {policy !== fn.default ? " •" : ""}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {POLICY_HINT[policy]}
                    </span>
                  </div>
                  <Select
                    className="w-44"
                    label=""
                    placeholder=""
                    helpText=""
                    value={policy}
                    disabled={saving}
                    onValueChange={(value: string) =>
                      setDraft({
                        ...draft,
                        policies: { ...draft.policies, [fn.key]: value as TabPolicy },
                      })
                    }
                  >
                    {(Object.keys(POLICY_LABEL) as TabPolicy[]).map((p) => (
                      <Select.Item key={p} value={p}>
                        {POLICY_LABEL[p]}
                      </Select.Item>
                    ))}
                  </Select>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {dirty && (
        <div className="sticky bottom-0 flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3 shadow-md">
          <span className="grow text-body font-body text-subtext-color">
            Unsaved changes — saving derives the roles, review policies and checker pools in one
            step.
          </span>
          <Button variant="neutral-secondary" disabled={saving} onClick={discard}>
            Discard
          </Button>
          <Button loading={saving} disabled={blocked} onClick={() => void save()}>
            Save team
          </Button>
        </div>
      )}

      <div className="flex w-full flex-col gap-3 pt-4">
        <div className="flex flex-col">
          <span className="text-heading-3 font-heading-3 text-default-font">Not on any tab</span>
          <span className="text-caption font-caption text-subtext-color">
            People with a stock role and no seat. New people are invited from master admin; once
            they exist here, seat them on the tabs above.
          </span>
        </div>
        {unseatedStaff.length === 0 && (
          <span className="text-body font-body text-subtext-color">
            Everyone is seated on a tab.
          </span>
        )}
        {unseatedStaff.map((member) => (
          <UnseatedRow
            key={member.userId}
            member={member}
            stockRoles={stockRoles}
            isSelf={Number(user?.id) === member.userId}
            onSaved={(message) => {
              setNotice(message);
              void load();
            }}
            onRemove={setRemoving}
          />
        ))}
      </div>

      {removing && (
        <DialogLayout open onOpenChange={(open: boolean) => !open && setRemoving(null)}>
          <div className="flex w-96 flex-col gap-4 px-6 py-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Deactivate {removing.name}?
            </span>
            <span className="text-body font-body text-subtext-color">
              They lose access to these books. Master admin is told to unassign them too.
            </span>
            <div className="flex w-full items-center justify-end gap-2">
              <Button variant="neutral-secondary" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button variant="destructive-primary" onClick={() => void confirmRemove()}>
                Deactivate
              </Button>
            </div>
          </div>
        </DialogLayout>
      )}
    </div>
  );
};
